import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import { useLanguage } from '../../localization';
import {
  type NewsItem,
  formatDate,
  itemDate,
  newsTagLabel,
  parseNewsList,
  sortPinnedThenNewest,
} from './shared';

type ListState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; items: NewsItem[] };

/**
 * NewsListPage — dedicated `/news` page listing all KF1-tagged posts,
 * pinned-first then newest, each card linking to its `/news/:id` detail page.
 * Mirrors the standalone-page chrome used by the rules page (Navigation, back
 * link, header, Footer).
 *
 * @returns {JSX.Element} The news list page
 */
export default function NewsListPage() {
  const { apiPath } = useLanguage();
  const [state, setState] = useState<ListState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });

    async function load() {
      try {
        const res = await fetch(apiPath('/api/news?tag=kf1'), { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: unknown = await res.json();
        setState({ status: 'ready', items: sortPinnedThenNewest(parseNewsList(data)) });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setState({ status: 'error' });
      }
    }

    void load();
    return () => controller.abort();
  }, [apiPath]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Navigation />
      <main className="relative z-10 pt-20 pb-16 flex-1">
        <div className="container mx-auto px-4">
          <Link
            to="/"
            className="mb-8 inline-block text-sm text-gray-400 transition-colors hover:text-red-400"
          >
            <i className="ri-arrow-left-s-line"></i> Back to Home
          </Link>

          <header className="mb-12 max-w-4xl">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-red-400">
              From the Front
            </p>
            <h1 className="mb-4 font-orbitron text-4xl font-bold horror-heading md:text-5xl">
              Latest <span className="text-red-500">News</span>
            </h1>
            <div className="mb-6 h-1 w-24 bg-red-600"></div>
            <p className="max-w-3xl text-xl leading-relaxed text-gray-300">
              Server updates, new perks, events, and announcements from the Killing Floor front.
            </p>
          </header>

          {state.status === 'loading' && <ListSkeleton />}
          {state.status === 'error' && <ListError />}
          {state.status === 'ready' &&
            (state.items.length === 0 ? (
              <ListEmpty />
            ) : (
              <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
                {state.items.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
            ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

/** List-page card linking to a post's detail page; pinned posts get a ★ badge. */
function NewsCard({ item }: { item: NewsItem }) {
  const { t } = useLanguage();
  const date = formatDate(itemDate(item));
  return (
    <Link to={`/news/${item.id}`} className="group block h-full">
      <Card
        variant="dark"
        className="flex h-full flex-col transition-colors duration-200 group-hover:border-red-800/60"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {item.pinned && (
              <span className="inline-flex items-center gap-1 rounded-full border border-red-700/60 bg-red-950/60 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-red-300">
                <i className="ri-star-fill text-[10px]"></i>
                {t('Pinned')}
              </span>
            )}
            {item.tag && (
              <span className="inline-flex items-center rounded-full border border-red-900/40 bg-red-900/30 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-400">
                {t(newsTagLabel(item.tag))}
              </span>
            )}
          </div>
          {date && (
            <time dateTime={itemDate(item)} className="text-xs font-medium text-gray-400">
              {date}
            </time>
          )}
        </div>

        <h2 className="mb-3 font-orbitron text-xl font-bold leading-snug text-white md:text-2xl">
          {item.title}
        </h2>

        {item.excerpt && (
          <p className="flex-1 leading-relaxed text-gray-300">{item.excerpt}</p>
        )}

        <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-red-400 transition-colors group-hover:text-red-300">
          {t('Read more')}
          <i className="ri-arrow-right-s-line transition-transform group-hover:translate-x-0.5"></i>
        </span>
      </Card>
    </Link>
  );
}

function ListSkeleton() {
  return (
    <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} variant="dark" className="animate-pulse">
          <div className="mb-4 flex items-center justify-between">
            <div className="h-5 w-16 rounded-full bg-gray-800"></div>
            <div className="h-3 w-20 rounded bg-gray-800"></div>
          </div>
          <div className="mb-3 h-5 w-3/4 rounded bg-gray-800"></div>
          <div className="space-y-2">
            <div className="h-3 w-full rounded bg-gray-800"></div>
            <div className="h-3 w-5/6 rounded bg-gray-800"></div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ListEmpty() {
  return (
    <Card variant="dark" className="mx-auto max-w-2xl border-dashed text-center">
      <i className="ri-newspaper-line mb-3 block text-4xl text-gray-500"></i>
      <p className="font-orbitron text-lg font-semibold text-gray-200">No news yet</p>
      <p className="mt-1 text-sm text-gray-400">
        Check back soon for server updates and announcements.
      </p>
    </Card>
  );
}

function ListError() {
  return (
    <Card variant="blood" className="mx-auto max-w-2xl text-center">
      <i className="ri-error-warning-line mb-3 block text-4xl text-red-400"></i>
      <p className="font-orbitron text-lg font-semibold text-white">Couldn't load the news feed</p>
      <p className="mt-1 text-sm text-gray-300">Please try again in a little while.</p>
    </Card>
  );
}
